import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Trash2, RefreshCw, Inbox } from "lucide-react";
import { verifyPasscode, fetchQuotes, updateQuoteStatus, deleteQuote } from "@/lib/api";

const STATUS = ["new", "in-progress", "closed"];

const StatusBadge = ({ status }) => {
  const map = {
    new: "bg-primary text-primary-foreground",
    "in-progress": "bg-secondary text-secondary-foreground border border-border",
    closed: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${map[status]}`}>
      {status}
    </span>
  );
};

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (pc) => {
    setLoading(true);
    try {
      const res = await fetchQuotes(pc);
      setQuotes(res.data);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem("jew_admin_pc");
    if (saved) {
      setPasscode(saved);
      setAuthed(true);
      load(saved);
    }
  }, [load]);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      await verifyPasscode(passcode);
      sessionStorage.setItem("jew_admin_pc", passcode);
      setAuthed(true);
      load(passcode);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid passcode");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("jew_admin_pc");
    setAuthed(false);
    setPasscode("");
    setQuotes([]);
  };

  const changeStatus = async (id, status) => {
    try {
      await updateQuoteStatus(id, status, passcode);
      setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await deleteQuote(id, passcode);
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <motion.form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md border border-border p-10"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
          <h1 className="font-bebas uppercase text-5xl text-foreground">Admin Access</h1>
          <p className="font-plex text-muted-foreground text-sm mt-2 mb-8">
            Enter the passcode to view inquiries.
          </p>
          <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Passcode
          </label>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            data-testid="admin-passcode-input"
            autoFocus
            className="w-full bg-transparent border-0 border-b border-border py-3 text-foreground font-plex text-2xl tracking-[0.3em] outline-none focus:border-primary transition-colors mb-8"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="w-full bg-foreground text-background py-4 font-mono text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Enter Dashboard
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-6 md:px-12 py-6 flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-xl z-10">
        <div>
          <h1 className="font-bebas uppercase text-3xl md:text-4xl text-foreground leading-none">
            Inquiry Dashboard
          </h1>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {quotes.length} total · {quotes.filter((q) => q.status === "new").length} new
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => load(passcode)}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-border px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-border px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary hover:border-primary transition-colors"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-10">
        {quotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-muted-foreground" data-testid="admin-empty">
            <Inbox size={48} strokeWidth={1} />
            <p className="font-mono text-xs uppercase tracking-widest mt-4">No inquiries yet</p>
          </div>
        ) : (
          <div className="grid gap-px bg-border border border-border md:grid-cols-2 xl:grid-cols-3">
            {quotes.map((q) => (
              <div key={q.id} data-testid={`quote-item-${q.id}`} className="bg-card p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bebas uppercase text-2xl text-foreground leading-none">
                      {q.name}
                    </div>
                    {q.company && (
                      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                        {q.company}
                      </div>
                    )}
                  </div>
                  <StatusBadge status={q.status} />
                </div>

                <div className="mt-4 font-mono text-xs text-muted-foreground space-y-1">
                  <div><span className="text-foreground">TEL</span> · {q.phone}</div>
                  <div className="break-all"><span className="text-foreground">EML</span> · {q.email}</div>
                  {q.location && <div><span className="text-foreground">LOC</span> · {q.location}</div>}
                </div>

                <div className="mt-4 border-t border-border pt-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                    {q.service}
                  </div>
                  <p className="font-plex text-sm text-secondary-foreground">{q.message}</p>
                </div>

                <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                  <select
                    value={q.status}
                    onChange={(e) => changeStatus(q.id, e.target.value)}
                    data-testid={`status-select-${q.id}`}
                    className="bg-transparent border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-foreground outline-none focus:border-primary"
                  >
                    {STATUS.map((s) => (
                      <option key={s} value={s} className="bg-background">{s}</option>
                    ))}
                  </select>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {new Date(q.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => remove(q.id)}
                    data-testid={`delete-${q.id}`}
                    className="text-muted-foreground hover:text-primary transition-colors"
                    aria-label="Delete inquiry"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
